import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import FileUploader from "../../components/admin/FileUploader";
import SeoFields from "../../components/admin/SeoFields";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, MagnifyingGlass, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { SortableProvider, SortableItems, SortableItem, DragHandle } from "../../components/admin/SortableList";
import Pagination from "../../components/public/Pagination";

const PER_PAGE = 25;

const empty = {
  name: "", category_id: "", short_description: "", description: "",
  composition: "", dosage_form: "", pack_size: "",
  image_url: "", stability_report_url: "", dcgi_approval_url: "",
  is_active: true, featured: false,
  slug: "",
  prescription_status: "", active_ingredient: "", manufacturer: "", therapeutic_area: "",
  meta_title: "", meta_description: "", meta_keywords: "", og_image: "", canonical_url: "", robots: "", locale: "en-IN",
};

export default function Products() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [page, setPage] = useState(1);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async (targetPage = page, targetQ = q) => {
    const params = { page: targetPage, limit: PER_PAGE };
    if (targetQ) params.q = targetQ;
    const { data } = await api.get("/admin/products", { params });
    // Server returns paginated envelope when ?page= is set
    const env = data || {};
    setItems(Array.isArray(env) ? env : env.items || []);
    setTotal(Array.isArray(env) ? env.length : env.total || 0);
    setPageCount(Array.isArray(env) ? 1 : env.pages || 1);
  };
  const loadCats = async () => {
    const { data } = await api.get("/admin/categories?kind=product");
    setCats(data);
  };
  useEffect(() => { loadCats(); }, []);
  useEffect(() => { load(page, q); }, [page]);

  const onSearch = (e) => { e.preventDefault(); setPage(1); load(1, q); };

  // ── CSV import/export ──
  const [importOpen, setImportOpen]   = useState(false);
  const [importFile, setImportFile]   = useState(null);
  const [importBusy, setImportBusy]   = useState(false);
  const [importResult, setImportResult] = useState(null);

  const downloadCsv = async (path, fallbackFilename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Try to honour Content-Disposition; else use fallback
      const disp = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(disp);
      a.download = m ? m[1] : fallbackFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onExport = () => downloadCsv("/admin/products-export.csv", "bonheur-products.csv");
  const onDownloadTemplate = () => downloadCsv("/admin/products-import-template.csv", "bonheur-products-template.csv");

  const runImport = async (dryRun) => {
    if (!importFile) { toast.error("Choose a .csv file first"); return; }
    setImportBusy(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post(`/admin/products-import?dry_run=${dryRun ? "true" : "false"}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      if (!dryRun) {
        toast.success(`Created ${data.created} · Updated ${data.updated}${data.errors?.length ? ` · ${data.errors.length} errors` : ""}`);
        await load();
      } else {
        toast.message(`Dry-run: would create ${data.created} · would update ${data.updated}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImportBusy(false);
    }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...empty, ...it, category_id: it.category_id || "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, category_id: form.category_id || null };
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
      } else {
        await api.post("/admin/products", payload);
      }
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete product?")) return;
    try { await api.delete(`/admin/products/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Products"
        subtitle="Manage product catalogue, technical documents and visibility."
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={onExport} variant="outline" data-testid="export-csv-btn" title="Download all products as CSV">
              <DownloadSimple size={16} className="mr-2" />Export CSV
            </Button>
            <Button onClick={() => { setImportFile(null); setImportResult(null); setImportOpen(true); }} variant="outline" data-testid="import-csv-btn">
              <UploadSimple size={16} className="mr-2" />Import CSV
            </Button>
            <Button onClick={openNew} data-testid="add-product-btn" className="bg-[#C2410C] hover:bg-[#9A3412] text-white">
              <Plus size={16} className="mr-2" />New product
            </Button>
          </div>
        }
      />
      <div className="p-8 space-y-4">
        <form onSubmit={onSearch} className="flex items-center gap-2 max-w-md">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or composition"
              data-testid="product-search"
              className="pl-9 bg-white"
            />
          </div>
          <Button type="submit" variant="outline" data-testid="product-search-btn">Search</Button>
        </form>

        <SortableProvider items={items} setItems={setItems} reorderEndpoint="/admin/reorder/products">
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="products-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F8F6] text-left text-[10px] tracking-[0.2em] uppercase text-[#4B5563]">
                <tr>
                  <th className="px-3 py-3 w-10"></th>
                  <th className="px-6 py-3">Product</th>
                  <th className="px-6 py-3">Category</th>
                  <th className="px-6 py-3">Composition</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {items.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-[#4B5563]">No products yet.</td></tr>}
                <SortableItems items={items}>
                  {items.map((it) => (
                    <SortableItem key={it.id} id={it.id} as="tr" data-testid={`product-row-${it.id}`}>
                      {(handleProps) => (
                        <>
                          <td className="px-3 py-3"><DragHandle handleProps={handleProps} /></td>
                          <td className="px-6 py-3">
                            <div className="font-medium text-[#1F2937]">{it.name}</div>
                            <div className="text-xs text-[#4B5563]">{it.dosage_form} {it.pack_size && `· ${it.pack_size}`}</div>
                          </td>
                          <td className="px-6 py-3 text-[#4B5563]">{it.category_name || "—"}</td>
                          <td className="px-6 py-3 text-[#4B5563] max-w-xs truncate">{it.composition || "—"}</td>
                          <td className="px-6 py-3">
                            <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ${
                              it.is_active ? "bg-[#C2410C]/10 text-[#C2410C]" : "bg-[#E2E8F0] text-[#4B5563]"
                            }`}>{it.is_active ? "Active" : "Inactive"}</span>
                            {it.featured && <span className="ml-2 text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded bg-[#0EA5E9]/20 text-[#0369A1]">Featured</span>}
                          </td>
                          <td className="px-6 py-3 text-right">
                            <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="text-[#4B5563] hover:text-[#C2410C] mr-3"><PencilSimple size={16} /></button>
                            <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                          </td>
                        </>
                      )}
                    </SortableItem>
                  ))}
                </SortableItems>
              </tbody>
            </table>
          </div>
        </SortableProvider>

        {total > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <div className="text-xs text-[#4B5563]" data-testid="admin-products-count">
              Showing <b>{(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)}</b> of <b>{total}</b> products
            </div>
            <Pagination
              page={page}
              pageCount={pageCount}
              onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              testIdPrefix="admin-products-pagination"
            />
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit product" : "New product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="prod-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Dosage form</Label>
                <Input value={form.dosage_form} onChange={(e) => setForm({ ...form, dosage_form: e.target.value })} placeholder="Tablet, Capsule…" data-testid="prod-dosage" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Composition</Label>
                <Input value={form.composition} onChange={(e) => setForm({ ...form, composition: e.target.value })} data-testid="prod-composition" />
              </div>
              <div className="space-y-2">
                <Label>Pack size</Label>
                <Input value={form.pack_size} onChange={(e) => setForm({ ...form, pack_size: e.target.value })} data-testid="prod-pack" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Short description</Label>
                <Textarea rows={2} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} data-testid="prod-short" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Description</Label>
                <Textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="prod-desc" />
              </div>
              <div className="md:col-span-2 space-y-4 pt-2 border-t border-[#E2E8F0]">
                <FileUploader
                  kind="image" accept="image/*" label="Product image"
                  currentUrl={form.image_url}
                  onUploaded={(url) => setForm({ ...form, image_url: url })}
                  testId="upload-image"
                />
                <FileUploader
                  kind="doc" accept=".pdf,.doc,.docx" label="Stability report (PDF)"
                  currentUrl={form.stability_report_url}
                  onUploaded={(url) => setForm({ ...form, stability_report_url: url })}
                  testId="upload-stability"
                />
                <FileUploader
                  kind="doc" accept=".pdf,.doc,.docx" label="DCGI approval (PDF)"
                  currentUrl={form.dcgi_approval_url}
                  onUploaded={(url) => setForm({ ...form, dcgi_approval_url: url })}
                  testId="upload-dcgi"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="prod-active" />
                <Label>Active</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="prod-featured" />
                <Label>Featured</Label>
              </div>
            </div>

            {/* Pharma E-E-A-T fields */}
            <div className="pt-4 border-t border-[#E2E8F0] space-y-4">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#4B5563] font-semibold">Pharma · Trust & Search Visibility</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prescription status</Label>
                  <Select value={form.prescription_status || "none"} onValueChange={(v) => setForm({ ...form, prescription_status: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="prod-rx"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="rx">Prescription only (Rx)</SelectItem>
                      <SelectItem value="otc">Over the counter (OTC)</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Active ingredient</Label><Input value={form.active_ingredient || ""} onChange={(e) => setForm({ ...form, active_ingredient: e.target.value })} data-testid="prod-ingredient" /></div>
                <div className="space-y-2"><Label>Manufacturer</Label><Input value={form.manufacturer || ""} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} data-testid="prod-manufacturer" /></div>
                <div className="space-y-2"><Label>Therapeutic area</Label><Input value={form.therapeutic_area || ""} onChange={(e) => setForm({ ...form, therapeutic_area: e.target.value })} data-testid="prod-therapeutic" /></div>
              </div>
            </div>

            <SeoFields form={form} setForm={setForm} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-product" className="bg-[#C2410C] hover:bg-[#9A3412]">Save product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Import CSV dialog ── */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-white max-w-2xl" data-testid="import-csv-dialog">
          <DialogHeader>
            <DialogTitle>Import products from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border border-[#E2E8F0] bg-[#F7F8F6] p-4 text-sm text-[#4B5563] space-y-2">
              <div><strong>How it works.</strong> Rows with a matching <code className="px-1 bg-white rounded">slug</code> are updated. Rows with an empty/missing slug create a new product (slug auto-generated from <code className="px-1 bg-white rounded">name</code>). Empty cells leave the existing value untouched.</div>
              <div className="text-xs"><strong>Required column:</strong> <code className="px-1 bg-white rounded">name</code>. &nbsp;Use <code className="px-1 bg-white rounded">category_slug</code> to assign category (e.g. <em>tablets</em>, <em>oral-liquid</em>).</div>
              <button type="button" onClick={onDownloadTemplate} className="text-[#C2410C] underline text-xs" data-testid="download-template-btn">Download blank template</button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="csv-file">Choose CSV file</Label>
              <input
                id="csv-file"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#0EA5E9] file:text-white file:font-semibold hover:file:bg-[#0284C7]"
                data-testid="csv-file-input"
              />
              {importFile && <div className="text-xs text-[#4B5563]">Selected: <strong>{importFile.name}</strong> ({(importFile.size / 1024).toFixed(1)} KB)</div>}
            </div>
            {importResult && (
              <div className="rounded-md border border-[#E2E8F0] p-4 text-sm space-y-2" data-testid="import-result">
                <div className="font-semibold">{importResult.dry_run ? "Dry-run result" : "Import result"}</div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-emerald-50 p-2 rounded"><div className="text-2xl font-bold text-emerald-700">{importResult.created}</div><div className="text-[10px] uppercase tracking-wider">Created</div></div>
                  <div className="bg-sky-50 p-2 rounded"><div className="text-2xl font-bold text-sky-700">{importResult.updated}</div><div className="text-[10px] uppercase tracking-wider">Updated</div></div>
                  <div className="bg-amber-50 p-2 rounded"><div className="text-2xl font-bold text-amber-700">{importResult.skipped}</div><div className="text-[10px] uppercase tracking-wider">Skipped</div></div>
                  <div className="bg-red-50 p-2 rounded"><div className="text-2xl font-bold text-red-700">{importResult.errors?.length || 0}</div><div className="text-[10px] uppercase tracking-wider">Errors</div></div>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="text-xs text-red-700 max-h-24 overflow-auto">
                    {importResult.errors.slice(0, 20).map((e, i) => <div key={i}>Line {e.line}: {e.error}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
            <Button variant="outline" disabled={!importFile || importBusy} onClick={() => runImport(true)} data-testid="dry-run-btn">
              {importBusy ? "Working…" : "Dry run"}
            </Button>
            <Button disabled={!importFile || importBusy} onClick={() => runImport(false)} className="bg-[#C2410C] hover:bg-[#9A3412]" data-testid="run-import-btn">
              {importBusy ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
